import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader } from "@/components/common";
import { TextField, SelectField, DateField } from "@/components/fields";
import { BILLING_FREQUENCIES } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Gear } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Settings() {
  const { company, refreshCompany } = useAuth();
  const [s, setS] = useState(null);
  const [c, setC] = useState(company || {});
  const [effDate, setEffDate] = useState("");
  const [amendments, setAmendments] = useState([]);

  const loadAmendments = () => api.get("/fee-amendments").then((r) => setAmendments(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data));
    loadAmendments();
    if (company) setC(company);
  }, [company]);

  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const setCo = (k, v) => setC((x) => ({ ...x, [k]: v }));

  const saveFees = async () => {
    try {
      const r = await api.put("/settings", { ...s, effective_date: effDate || undefined });
      setS(r.data); setEffDate(""); loadAmendments();
      toast.success("Fee settings saved");
    }
    catch (e) { toast.error(apiError(e)); }
  };
  const cancelAmendment = async (id) => {
    try { await api.delete(`/fee-amendments/${id}`); loadAmendments(); toast.success("Scheduled change cancelled"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveCompany = async () => {
    try { await api.put(`/companies/${company.id}`, c); await refreshCompany(); toast.success("Company saved"); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!s) return <Loader />;

  return (
    <div>
      <PageHeader title="Settings" subtitle="Fees, VAT, billing & company profile" icon={Gear} />
      <Tabs defaultValue="fees">
        <TabsList className="mb-4">
          <TabsTrigger value="fees" data-testid="tab-fees">Fees & Commissions</TabsTrigger>
          <TabsTrigger value="company" data-testid="tab-company">Company & Banking</TabsTrigger>
        </TabsList>

        <TabsContent value="fees">
          <Card className="p-6 max-w-2xl">
            <div className="grid sm:grid-cols-2 gap-5">
              <TextField label="Default Management Fee (%)" type="number" value={s.management_fee_pct} onChange={(v) => set("management_fee_pct", v)} testId="mgmt-fee" />
              <TextField label="Sourcing Commission (% of Mgmt Fee)" type="number" value={s.sourcing_commission_pct} onChange={(v) => set("sourcing_commission_pct", v)} testId="sourcing-pct" />
              <TextField label="VAT Rate (%)" type="number" value={s.vat_rate} onChange={(v) => set("vat_rate", v)} />
              <SelectField label="Default Billing Frequency" value={s.default_billing_frequency} onChange={(v) => set("default_billing_frequency", v)} options={BILLING_FREQUENCIES} />
              <DateField label="Effective From (optional)" value={effDate} onChange={setEffDate} testId="eff-date" />
              <div className="flex items-center gap-3 pt-2">
                <Switch checked={!!s.vat_inclusive} onCheckedChange={(v) => set("vat_inclusive", v)} data-testid="vat-inclusive" />
                <Label>Management fee is VAT inclusive</Label>
              </div>
            </div>
            <div className="mt-4 text-xs text-muted-foreground bg-secondary/50 rounded-md p-3 space-y-1">
              <p><b>First Rent:</b> 100% of agreed rent, split as — Mgmt Fee (company) + Sourcing (sourcing staff) + Letting (letting staff, the remainder). Sourcing &amp; Letting are charged once, on first rent.</p>
              <p><b>Per-property fee:</b> set a Mgmt Fee % on any Property to override this default. Leave blank to use the default above.</p>
              <p><b>Effective From:</b> set a date to schedule a rate change. Already-charged fees &amp; commissions are never recalculated.</p>
            </div>
            <Button className="mt-6" onClick={saveFees} data-testid="save-fees-btn">Save Fee Settings</Button>
          </Card>

          {amendments.length > 0 && (
            <Card className="p-6 max-w-2xl mt-4" data-testid="amendment-history">
              <div className="text-sm font-semibold text-primary mb-3">Rate Amendment History</div>
              <div className="space-y-2">
                {amendments.map((a) => {
                  const eff = (a.effective_date || "").slice(0, 10);
                  const scheduled = eff > new Date().toISOString().slice(0, 10);
                  return (
                    <div key={a.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm border-b pb-2" data-testid={`amendment-${a.id}`}>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${scheduled ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{scheduled ? "Scheduled" : "Applied"}</span>
                      <span className="font-medium">{eff}</span>
                      {a.management_fee_pct != null && <span>Mgmt {a.management_fee_pct}%</span>}
                      {a.sourcing_commission_pct != null && <span>Sourcing {a.sourcing_commission_pct}%</span>}
                      {a.vat_rate != null && <span>VAT {a.vat_rate}%</span>}
                      {scheduled && (
                        <button onClick={() => cancelAmendment(a.id)} data-testid={`cancel-amendment-${a.id}`}
                          className="ml-auto text-xs text-red-600 hover:underline">Cancel</button>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="company">
          <Card className="p-6 max-w-2xl">
            <div className="grid sm:grid-cols-2 gap-5">
              <TextField label="Company Name" value={c.name} onChange={(v) => setCo("name", v)} />
              <TextField label="Currency" value={c.currency} onChange={(v) => setCo("currency", v)} testId="currency" />
              <TextField label="Location" value={c.location} onChange={(v) => setCo("location", v)} />
              <TextField label="Phone" value={c.phone} onChange={(v) => setCo("phone", v)} />
              <TextField label="Email" value={c.email} onChange={(v) => setCo("email", v)} />
              <TextField label="Logo URL" value={c.logo_url} onChange={(v) => setCo("logo_url", v)} />
              <TextField label="Address" value={c.address} onChange={(v) => setCo("address", v)} className="sm:col-span-2" />
            </div>
            <div className="text-sm font-semibold text-primary mt-6 mb-3">Banking & M-PESA</div>
            <div className="grid sm:grid-cols-2 gap-5">
              <TextField label="M-PESA Paybill" value={c.mpesa_paybill} onChange={(v) => setCo("mpesa_paybill", v)} testId="paybill" />
              <TextField label="Bank Name" value={c.bank_name} onChange={(v) => setCo("bank_name", v)} />
              <TextField label="Branch" value={c.bank_branch} onChange={(v) => setCo("bank_branch", v)} />
              <TextField label="SWIFT Code" value={c.bank_swift} onChange={(v) => setCo("bank_swift", v)} />
              <TextField label="Account Name" value={c.account_name} onChange={(v) => setCo("account_name", v)} />
              <TextField label="Account Number" value={c.account_number} onChange={(v) => setCo("account_number", v)} />
            </div>
            <Button className="mt-6" onClick={saveCompany} data-testid="save-company-btn">Save Company</Button>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
